import React, { Component } from 'react';
import { Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import withHooks from '../../core/withHooks';
import Inherit from '../../core/inherit';
import Page from '../common/page/Page';
import NoPermissionFallback from '../common/roleGuard/elements/NoPermissionFallback';
import { SCOPES } from '../../constants/scopes.constants';
import DashboardController from './dashboard.controller';
import DashboardTabs from './elements/DashboardTabs';
import DashboardPeriodControls from './elements/DashboardPeriodControls';
import {
    DASHBOARD_TABS,
    DEFAULT_TAB_KEY,
    DASHBOARD_DEFAULT_TIMEFRAME,
    USER_SUMMARY_PAGE_SIZE,
} from './dashboard.constants';
import styles from './styles/dashboard.module.scss';

class Dashboard extends Inherit(DashboardController, Component) {
    state = {
        user: {},
        dashboardData: null,
        tabCache: {},
        loading: true,
        dateRange: null,
        timeFrame: DASHBOARD_DEFAULT_TIMEFRAME,
        ytdReport: {},
        showConfigurationReminder: false,
        activeTab: DEFAULT_TAB_KEY,
        userSummary: {
            rows: [],
            pageInfo: { current: 1, perPage: USER_SUMMARY_PAGE_SIZE, total: 0 },
            loading: false,
        },
        transactionSummary: {
            rows: [],
            pageInfo: { current: 1, perPage: USER_SUMMARY_PAGE_SIZE, total: 0 },
            loading: false,
        },
        funnel: {
            steps: [],
            loading: false,
        },
        cohortModal: {
            open: false,
            metric: null,
            users: [],
            count: 0,
            query: '',
            sortBy: 'name',
            loading: false,
        },
        sessionsDrawer: {
            open: false,
            loading: false,
            events: [],
            count: 0,
            query: '',
            platform: 'all',
            type: 'all',
            dateRange: null,
        },
        userAdoptionMetrics: {
            activeFrequency: 'monthly',
            data: {},
            loading: false,
        },
        engagementSummaryMetrics: {
            activeFrequency: 'monthly',
            data: {},
            loading: false,
        },
    };

    componentDidUpdate(prevProps, prevState) {
        const { showConfigurationReminder } = this.state;
        const { notificationApi } = this.props;
        const notificationKey = 'configuration-reminder';

        if (showConfigurationReminder && !prevState.showConfigurationReminder) {
            notificationApi.info({
                key: notificationKey,
                message: 'Complete your configuration',
                description: 'Finish setting up your Clockout configuration so your users can access the experience.',
                placement: 'topRight',
                duration: 0,
                btn: (
                    <Button type="primary" size="small" onClick={() => this.handleConfigurationReminderAction(notificationKey)}>
                        Go to configurations
                    </Button>
                ),
                onClose: () => this.handleConfigurationReminderDismiss(),
                closeIcon: null
            });
        } else if (!showConfigurationReminder && prevState.showConfigurationReminder) {
            notificationApi.destroy(notificationKey);
        }
    }

    handleConfigurationReminderAction(notificationKey) {
        const { notificationApi } = this.props;
        notificationApi.destroy(notificationKey);
        this.handleConfigurationReminderDismiss();
        this.navigateConfigurations();
    }

    render() {
        const {
            activeTab,
            dashboardData,
            loading,
            timeFrame,
            dateRange,
            userAdoptionMetrics,
            engagementSummaryMetrics,
            userSummary,
            transactionSummary,
            funnel,
            cohortModal,
            sessionsDrawer,
        } = this.state;
        const accessibleTabs = this.getAccessibleDashboardTabs();
        // Screen scope without a single tab scope grants nothing to look at.
        // Deny outright instead of falling back to a tab: the strip would
        // render empty while that tab's content showed underneath it.
        // Gated on `this.user` so an unhydrated session waits for Page's own
        // loading deferral rather than being denied on a not-yet-filled
        // scope list.
        if (this.user && !accessibleTabs.length) {
            return (
                <Page
                    scope={SCOPES.DASHBOARD_VIEW}
                    notificationContextHolder={this.props.notificationContextHolder}
                >
                    <NoPermissionFallback />
                </Page>
            );
        }
        const currentTab = accessibleTabs.find((tab) => tab.key === activeTab) || accessibleTabs[0] || DASHBOARD_TABS[0];
        const cohort = {
            ...cohortModal,
            rows: this.getCohortRows(),
            onOpen: this.openCohortModal.bind(this),
            onClose: this.closeCohortModal.bind(this),
            onSearch: this.handleCohortSearch.bind(this),
            onSort: this.handleCohortSort.bind(this),
            onExport: this.exportCohortUsers.bind(this),
        };
        const sessions = {
            ...sessionsDrawer,
            rows: this.getSessionRows(),
            platformOptions: this.getSessionPlatformOptions(),
            onOpen: this.openSessionsDrawer.bind(this),
            onClose: this.closeSessionsDrawer.bind(this),
            onSearch: this.handleSessionsSearch.bind(this),
            onPlatformChange: this.handleSessionsPlatform.bind(this),
            onTypeChange: this.handleSessionsType.bind(this),
            onDateRangeChange: this.handleSessionsDateRange.bind(this),
            onExport: this.exportSessionEvents.bind(this),
        };
        return (
            <Page
                title={currentTab.title}
                description={currentTab.description}
                tabs={accessibleTabs.map(({ key, label, icon }) => ({ key, label, icon }))}
                onTabChange={(key) => this.handleTabChange(key)}
                scope={SCOPES.DASHBOARD_VIEW}
                notificationContextHolder={this.props.notificationContextHolder}
                action={
                    <div className={styles.periodToolbar}>
                        <DashboardPeriodControls
                            timeFrame={timeFrame}
                            dateRange={dateRange}
                            onTimeFrameChange={(value) => this.handleTimeFrameChange(value)}
                            onDateRangeChange={(dates) => this.handleDateRangeChange(dates)}
                        />
                        <Button
                            type="default"
                            icon={<ReloadOutlined />}
                            onClick={() => this.fetchData()}
                            loading={loading}
                        >
                            Refresh
                        </Button>
                    </div>
                }
            >
                <div className={styles.dashboardContainer}>
                    <DashboardTabs
                        activeTab={activeTab}
                        dashboardData={dashboardData}
                        loading={loading}
                        userAdoptionMetrics={userAdoptionMetrics}
                        engagementSummaryMetrics={engagementSummaryMetrics}
                        userSummary={userSummary}
                        transactionSummary={transactionSummary}
                        funnel={funnel}
                        cohort={cohort}
                        sessions={sessions}
                        onUserAdoptionFrequencyChange={(frequency) =>
                            this.handleUserAdoptionFrequencyChange(frequency)
                        }
                        onEngagementSummaryFrequencyChange={(frequency) =>
                            this.handleEngagementSummaryFrequencyChange(frequency)
                        }
                        onUserSummaryTableChange={this.handleUserSummaryTableChange.bind(this)}
                        onTransactionSummaryTableChange={this.handleTransactionSummaryTableChange.bind(this)}
                    />
                </div>
            </Page>
        );
    }
}

export default withHooks(Dashboard);
